/**
 * Las reglas del tapado y de qué foto sigue, en un solo lugar.
 *
 * Decisiones del usuario (2026-10-13):
 *  - Se tapa SOLO lo que tiene ubicación cierta: la plantilla del modelo sobre
 *    una tarjeta ubicada (`zonasATapar` garantizado) y el código 2D en el lugar
 *    donde se lo acaba de decodificar. Nada adivinado: el tapado por el rótulo
 *    «Trámite» que leía el OCR pintaba líneas negras corridas y se fue.
 *  - El tapado NO decide si la foto sigue: sin el trámite ubicado, no se tapa y
 *    se deja seguir. Lo que decide son las tres condiciones de `aceptacion.ts`.
 */
import type { Tapado } from "./reader/redaccion";
import type { Rectangulo } from "./reader/types";

/**
 * Si un frente cuyo Nº de Trámite no se pudo ubicar (y por eso no se tapó) puede
 * seguir. Es la línea para cambiar si el usuario decide otra cosa.
 */
export function frenteSinTramitePuedeSeguir(): boolean {
  return true;
}

/**
 * Si para aceptar las dos caras hace falta CONFIRMAR que son del mismo DNI (el
 * mismo número leído en las dos) o alcanza con que no se contradigan. Alcanza:
 * decisión del usuario (2026-10-13). Una contradicción visible entre lo leído de
 * una cara y lo probado de la otra sí rechaza (aceptacion.ts). Confirmarlo dejaba
 * afuera, en el corpus, cinco pares reales legibles cuyo frente no deja leer el
 * número.
 */
export function mismoDniDebeConfirmarse(): boolean {
  return false;
}

/**
 * Lo que se pinta de negro en el frente que se entrega: la plantilla, solo si es
 * garantía; el lugar del código 2D que se decodificó, que lleva el trámite
 * adentro; y el Nº de Trámite impreso donde el OCR lo encontró buscando el
 * número que dio el código, que reemplaza a su zona de la plantilla.
 */
export function zonasQueSeTapanEnElFrente(
  tapado: Tapado,
  zonasDelCodigo: Rectangulo[],
  tramiteUbicado: Rectangulo | null = null
): Rectangulo[] {
  const plantilla = tapado.garantizado
    ? tapado.zonas.map((z) => (tramiteUbicado && z.campo === "numeroTramite" ? tramiteUbicado : z.caja))
    : [];
  const ubicado = tramiteUbicado && !plantilla.includes(tramiteUbicado) ? [tramiteUbicado] : [];
  return [...plantilla, ...ubicado, ...zonasDelCodigo];
}

/**
 * Qué tapa la marca negra de una cara, para decirlo tal cual (KAN-460): el Nº de Trámite, el código
 * de barras o los dos. Sale de las mismas fuentes que se pintan: en el frente, la plantilla solo si
 * es garantía, el código decodificado y el trámite que ubicó el OCR (zonasQueSeTapanEnElFrente); en
 * el dorso, sus zonas.
 */
export interface LoQueTapaLaMarca {
  tramite: boolean;
  codigo: boolean;
}

const CAMPO_DEL_TRAMITE = "numeroTramite";
const CAMPO_DEL_CODIGO = "codigoDeBarras";

export function loQueTapaElFrente(
  tapado: Tapado,
  zonasDelCodigo: Rectangulo[],
  tramiteUbicado: Rectangulo | null = null
): LoQueTapaLaMarca {
  const plantilla = tapado.garantizado ? tapado.zonas : [];
  return {
    tramite: !!tramiteUbicado || plantilla.some((z) => z.campo === CAMPO_DEL_TRAMITE),
    codigo: zonasDelCodigo.length > 0 || plantilla.some((z) => z.campo === CAMPO_DEL_CODIGO),
  };
}

export function loQueTapaElDorso(tapado: Tapado): LoQueTapaLaMarca {
  return {
    tramite: tapado.zonas.some((z) => z.campo === CAMPO_DEL_TRAMITE),
    codigo: tapado.zonas.some((z) => z.campo === CAMPO_DEL_CODIGO),
  };
}
